import type { ApiEndpoint } from "@/shared/api-endpoint";
import { isObject } from "@/shared/utils/typeGuards";

/**
 * The signed-in person's Splice profile, for the website's account menu. The
 * Auth0 profile has a name and an email but neither the Splice username nor
 * the avatar from splice.com; the gateway has both, and answers for whoever
 * the bearer token belongs to.
 */
export const kSpliceProfileRoute = "/splice-profile";

export type SpliceProfileRequestBody = Record<string, never>;

export interface SpliceProfile {
    readonly username: string;
    readonly name?: string;
    readonly email?: string;
    readonly avatarUrl?: string;
    readonly uuid?: string;
}

export interface SpliceProfileSuccessResponse {
    readonly ok: true;
    readonly profile: SpliceProfile;
}

const isOptionalString = (value: unknown): value is string | undefined =>
    value === undefined || typeof value === "string";

export const isSpliceProfile = (value: unknown): value is SpliceProfile =>
    isObject(value) &&
    typeof value.username === "string" &&
    isOptionalString(value.name) &&
    isOptionalString(value.email) &&
    isOptionalString(value.avatarUrl) &&
    isOptionalString(value.uuid);

export const isSpliceProfileRequestBody = (value: unknown): value is SpliceProfileRequestBody =>
    isObject(value);

export const isSpliceProfileSuccessResponse = (
    value: unknown,
): value is SpliceProfileSuccessResponse =>
    isObject(value) && value.ok === true && isSpliceProfile(value.profile);

export const kSpliceProfileEndpoint: ApiEndpoint<
    SpliceProfileRequestBody,
    SpliceProfileSuccessResponse
> = {
    route: kSpliceProfileRoute,
    label: "Splice Profile",
    source: "Splice",
    // Platform: who is signed in is chrome, not a capability, so it reports
    // no capability events.
    category: "Platform",
    description: "Returns the signed-in user's Splice username and avatar for the account menu.",
    isRequestBody: isSpliceProfileRequestBody,
    isSuccessResponse: isSpliceProfileSuccessResponse,
};
